/**
 * useNotifications — UAT-3A in-app notifications hook.
 *
 * Source of truth for the bell badge + drawer list.
 *
 * Wiring:
 *   • REST: GET /api/notifications, /unread-count, PUT /{id}/read,
 *           POST /read-all.
 *   • Realtime: listens to `snappipit:notification` and
 *           `snappipit:notification_unread_changed` window CustomEvents
 *           dispatched by `useWebSocketChat` when the chat socket
 *           receives `{type: "notification" | "unread_count_changed"}`.
 *
 * The hook is intentionally lightweight — it doesn't persist anywhere,
 * doesn't poll on a tight interval, and is safe to mount in multiple
 * components (the bell + the drawer can both consume independently).
 *
 * On enabled=false (e.g. user not authenticated), no requests are made.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationsAPI, sharedGet } from '../lib/api';

const REFRESH_INTERVAL = 60_000; // 60s safety re-sync (covers missed WS)

// PWA Badging API — sets/clears the app-icon badge where supported
// (installed PWA on Chrome/Edge/Safari). Silently no-ops elsewhere.
function applyAppBadge(count) {
  try {
    if (typeof navigator === 'undefined') return;
    if (count > 0 && navigator.setAppBadge) {
      navigator.setAppBadge(count);
    } else if (navigator.clearAppBadge) {
      navigator.clearAppBadge();
    }
  } catch {
    /* unsupported — ignore */
  }
}

export default function useNotifications(enabled = true) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'

  const refreshTimerRef = useRef(null);
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  // Keep the PWA app-icon badge in sync with the unread count across every
  // path (REST refresh, WS push, optimistic mark-read). Single source of truth.
  useEffect(() => {
    if (!enabled) return;
    applyAppBadge(unread);
  }, [unread, enabled]);

  // ── REST: list ─────────────────────────────────────────────────────
  const fetchList = useCallback(async (opts = {}) => {
    if (!enabledRef.current) return;
    const nextPage = opts.page ?? 1;
    const nextFilter = opts.filter ?? filter;
    setLoading(true);
    try {
      const params = { page: nextPage, page_size: 30 };
      if (nextFilter === 'unread') params.read = false;
      const res = await notificationsAPI.list(params);
      const data = res.data || {};
      setItems((previous) => {
        const incoming = data.items || [];
        if (nextPage === 1) return incoming;
        const seen = new Set(previous.map((item) => item.id));
        return [...previous, ...incoming.filter((item) => !seen.has(item.id))];
      });
      setUnread(data.unread || 0);
      setTotal(data.total || 0);
      setPage(nextPage);
    } catch {
      // silently — user may have logged out
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // ── REST: unread count + by_category ───────────────────────────────
  const refreshUnread = useCallback(async () => {
    if (!enabledRef.current) return;
    try {
      const res = await sharedGet('/notifications/unread-count');
      setUnread(res.data?.unread || 0);
      setByCategory(res.data?.by_category || {});
    } catch {
      // ignore
    }
  }, []);

  // ── Mark single notification as read (optimistic) ──────────────────
  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      const res = await notificationsAPI.markRead(id);
      if (typeof res.data?.unread === 'number') setUnread(res.data.unread);
    } catch {
      // rollback would be nice but not critical — next refresh corrects it
      refreshUnread();
    }
  }, [refreshUnread]);

  // ── Mark all as read ───────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    setByCategory({});
    try {
      await notificationsAPI.markAllRead();
    } catch {
      refreshUnread();
    }
  }, [refreshUnread]);

  const clearAll = useCallback(async () => {
    await notificationsAPI.clearAll();
    setItems([]);
    setUnread(0);
    setByCategory({});
    setTotal(0);
    setPage(1);
  }, []);

  // ── Realtime: listen to WS events re-emitted by useWebSocketChat ───
  useEffect(() => {
    if (!enabled) return undefined;

    const onIncoming = (event) => {
      const n = event.detail;
      if (!n || !n.id) return;
      setItems((prev) => {
        if (prev.some((x) => x.id === n.id)) return prev; // idempotent
        return [n, ...prev].slice(0, 200);
      });
      if (!n.read) {
        setUnread((u) => u + 1);
        setByCategory((c) => ({
          ...c,
          [n.category || 'general']: (c[n.category || 'general'] || 0) + 1,
        }));
      }
    };

    const onUnreadChanged = () => {
      // Defer to authoritative server count
      refreshUnread();
    };

    window.addEventListener('snappipit:notification', onIncoming);
    window.addEventListener('snappipit:notification_unread_changed', onUnreadChanged);
    return () => {
      window.removeEventListener('snappipit:notification', onIncoming);
      window.removeEventListener('snappipit:notification_unread_changed', onUnreadChanged);
    };
  }, [enabled, refreshUnread]);

  // ── Initial load + safety re-sync timer ────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    refreshUnread();
    refreshTimerRef.current = setInterval(refreshUnread, REFRESH_INTERVAL);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [enabled, refreshUnread]);

  // ── Switch filter (and refetch) ────────────────────────────────────
  const setFilterAndReload = useCallback((nextFilter) => {
    setFilter(nextFilter);
    fetchList({ page: 1, filter: nextFilter });
  }, [fetchList]);

  return {
    items,
    unread,
    byCategory,
    loading,
    page,
    total,
    filter,
    fetchList,
    refreshUnread,
    markRead,
    markAllRead,
    clearAll,
    loadMore: () => fetchList({ page: page + 1, filter }),
    hasMore: items.length < total,
    setFilter: setFilterAndReload,
  };
}
